/**
 * Routing rules :
 * - if using a meta.permissions, it MUST be an array
 * - please keep this file simple as possible, and avoid to add logic
 */
import { useDebounceFn } from '@vueuse/core';
import { createRouter, createWebHistory } from 'vue-router';

import Reporter from '@/api/clients/reporter/reporter';
import { useScrollControl } from '@/composables/useIsScrolled';
import { t } from '@/config/i18n';
import { getSpacesRole } from '@/helpers/spaces';
import { useAuthStore } from '@/stores/auth';
import { useGlobalStore } from '@/stores/global';

import { DEV_ROUTE } from './dev/index';
import { FORBIDDEN_ROUTE, FORBIDDEN_SPACES_ROUTE, NOTFOUND_ROUTE } from './errors';
import { LOGOUT_ROUTE } from './logout';
import { RECRUITER_ROUTES } from './recruiter';

// add specific TS config
declare module 'vue-router' {
  interface RouteMeta {
    permissions?: string[];
    title?: string;
  }
}
// Import utils
export const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      name: 'home',
      redirect: { name: 'recruiter' }
    },
    // recruiter
    RECRUITER_ROUTES,

    // errors
    FORBIDDEN_ROUTE,
    FORBIDDEN_SPACES_ROUTE,
    NOTFOUND_ROUTE,

    // logout
    LOGOUT_ROUTE,

    // dev
    DEV_ROUTE
  ]
});

const scrollTop = useDebounceFn(() => {
  window.scrollTo({
    top: 0,
    behavior: 'smooth'
  });
}, 200);

/** GUARDS **/
router.beforeEach((to, from) => {
  /* If the last route is an internal one, add it to the meta data to allow retrieval in component */
  if (from.name) {
    to.meta.from = from.fullPath;
  }
  /*
        'to.matched' is an array that reference all result for the requested path ...
        If empty array, redirect to the not found
    */
  if (to.matched.length === 0) {
    return { name: 'notfound' };
  } else {
    // Check permissions before each navigation
    if (to.meta?.permissions) {
      // Collect all required permissions from the matched routes hierarchy
      // Ensures that both parent and child routes contribute to the final permissions array
      const permissions = to.matched.reduce<string[]>((acc, record) => {
        if (record.meta?.permissions) {
          acc.push(...record.meta.permissions);
        }
        return acc;
      }, []);

      const accessAuthorized = useAuthStore().hasPermissionsList(permissions);

      if (accessAuthorized) {
        // Change meta title if it exists on route config
        if (to.meta?.title) {
          document.title = t(to.meta.title);
        }

        // When a query change we don't want to scroll to top
        const { shouldScroll } = useScrollControl();
        if (shouldScroll()) {
          scrollTop();
        }

        const roleState = getSpacesRole();
        switch (roleState) {
          case 'noScopes':
          case 'validSpaces':
            return true;
          case 'invalidSpaces':
            return { name: 'forbidden-spaces' };
          case 'noCookies':
            window.location.assign(`${useGlobalStore()?.auth_url}/spaces`);
            return false;
          default:
            return true;
        }
      } else {
        return { name: 'forbidden' };
      }
    }
  }
});

// After each redirection of each page
router.afterEach((to, _from) => {
  // end time reporter
  if (_from.name) {
    Reporter().reportData(`Leave ${_from.name.toString()}`);
  }

  // start new time reporter, and enter log
  if (to.name) {
    Reporter().startTimeEvent(`Leave ${to.name.toString()}`);

    Reporter().reportData(`Enter ${to.name.toString()}`, {
      entry_point: _from.name
    });
  }
});
